import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Building2, Users2, ShieldAlert, Star, MapPin, DollarSign, Activity, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useSettings } from "../lib/settings";
import { getContrastText } from "../lib/theme";

export default function MasterReports() {
  const navigate = useNavigate();
  const user = auth.current();
  const { settings } = useSettings();
  const secondary = settings.secondary_color || "#7C3AED";
  const onSecondary = getContrastText(secondary);
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loadingMoreAudit, setLoadingMoreAudit] = useState(false);
  const [tab, setTab] = useState("sos"); // "sos" | "feedback" | "actividad"
  const tabsRef = useRef(null);
  const goToTab = (t) => {
    setTab(t);
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ACTION_LABELS = {
    agencia_creada: "Agencia creada", agencia_editada: "Agencia editada",
    agencia_activada: "Agencia reactivada", agencia_desactivada: "Agencia desactivada",
    admin_creado: "Admin creado", admin_activado: "Admin habilitado", admin_desactivado: "Admin deshabilitado",
    codigo_creado: "Código creado", codigo_activado: "Código habilitado", codigo_desactivado: "Código deshabilitado",
  };

  useEffect(() => {
    if (!user || user.role !== "master") { navigate("/admin/login"); return; }
    api.get("/master/reports").then(({ data }) => setData(data)).catch(() => {});
    api.get("/master/revenue").then(({ data }) => setRevenue(data)).catch(() => {});
    api.get("/master/audit-log", { params: { skip: 0, limit: 50 } })
      .then(({ data }) => { setAuditLog(data.items); setAuditTotal(data.total); })
      .catch(() => {});
  }, []);

  const loadMoreAudit = async () => {
    setLoadingMoreAudit(true);
    try {
      const { data } = await api.get("/master/audit-log", { params: { skip: auditLog.length, limit: 50 } });
      setAuditLog((prev) => [...prev, ...data.items]);
      setAuditTotal(data.total);
    } catch {
      // silencioso: el botón sigue ahí para reintentar
    } finally {
      setLoadingMoreAudit(false);
    }
  };

  if (!user) return null;

  const s = data?.summary;

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 space-y-10">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Master · Tulkas</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            <span className="text-[var(--brand-cyan,#FF85AC)]">Reportes</span> globales
          </h1>
          <p className="text-neutral-400 mt-3 max-w-2xl">
            SOS y calificaciones de todas las agencias, cada dato con su agencia identificada. Solo
            lectura — la operación del día a día es de cada agencia.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Agencias activas", value: s ? `${s.active_agency_count}/${s.agency_count}` : "—", icon: Building2, onClick: () => navigate("/master") },
            { label: "Viajeros totales", value: s?.traveler_count ?? "—", icon: Users2, onClick: () => navigate("/master") },
            { label: "SOS activas ahora", value: s?.active_sos_count ?? "—", icon: ShieldAlert, onClick: () => goToTab("sos") },
            { label: "Alertas registradas", value: data?.sos_alerts?.length ?? "—", icon: BarChart3, onClick: () => goToTab("sos") },
          ].map(({ label, value, icon: Icon, onClick }) => (
            <button
              key={label}
              type="button"
              onClick={onClick}
              data-testid={`master-summary-${label}`}
              className="text-left rounded-2xl p-5 border border-white/10 hover:brightness-110 active:brightness-95 transition-[filter] cursor-pointer"
              style={{ backgroundColor: secondary, color: onSecondary }}
            >
              <Icon className="w-4 h-4 mb-3 opacity-80" strokeWidth={1.5} />
              <p className="font-display text-2xl">{value}</p>
              <p className="text-xs mt-1 opacity-70">{label}</p>
            </button>
          ))}
        </div>

        {revenue && (
          <article className="glass rounded-3xl p-7 border border-white/10">
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
                <h2 className="font-display text-xl">Ingresos estimados</h2>
              </div>
              <p className="font-display text-3xl">
                ${revenue.mrr_estimate_mxn.toLocaleString()} <span className="text-sm text-neutral-500">MXN/mes</span>
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {Object.entries(revenue.by_plan).map(([plan, d]) => (
                <div key={plan} className="rounded-xl bg-black/40 border border-white/10 p-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">{plan}</p>
                  <p className="font-display text-lg text-white">{d.count} <span className="text-xs text-neutral-500">agencias</span></p>
                  <p className="text-xs text-[var(--brand-cyan,#FF85AC)]">${d.subtotal_mxn.toLocaleString()} MXN</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-600">{revenue.note}</p>
          </article>
        )}

        <div ref={tabsRef} className="flex items-center gap-6 border-b border-white/10 scroll-mt-6">
          <button
            onClick={() => setTab("sos")}
            data-testid="reports-tab-sos"
            className={`pb-3 text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              tab === "sos" ? "border-[var(--brand-cyan,#FF85AC)] text-[var(--brand-cyan,#FF85AC)]" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            SOS ({data?.sos_alerts?.length ?? 0})
          </button>
          <button
            onClick={() => setTab("feedback")}
            data-testid="reports-tab-feedback"
            className={`pb-3 text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              tab === "feedback" ? "border-[var(--brand-cyan,#FF85AC)] text-[var(--brand-cyan,#FF85AC)]" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            Feedback ({data?.feedbacks?.length ?? 0})
          </button>
          <button
            onClick={() => setTab("actividad")}
            data-testid="reports-tab-actividad"
            className={`pb-3 text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              tab === "actividad" ? "border-[var(--brand-cyan,#FF85AC)] text-[var(--brand-cyan,#FF85AC)]" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            Actividad ({auditTotal})
          </button>
        </div>

        {tab === "sos" && (
          <div className="space-y-3" data-testid="reports-sos-list">
            {(data?.sos_alerts || []).length === 0 && (
              <p className="text-sm text-neutral-500">Sin alertas SOS registradas todavía.</p>
            )}
            {(data?.sos_alerts || []).map((a) => (
              <div key={a.id} className="glass rounded-2xl p-5 border border-white/10 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white">{a.sender_name}</p>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${
                      a.status === "active"
                        ? "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10"
                        : "text-neutral-500 border-white/10"
                    }`}>
                      {a.status === "active" ? "Activa" : "Resuelta"}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">{a.agency_name} · {a.created_at}</p>
                  {a.maps_url && (
                    <a href={a.maps_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-cyan,#FF85AC)] hover:underline inline-flex items-center gap-1 mt-1.5">
                      <MapPin className="w-3 h-3" /> Ver ubicación
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "feedback" && (
          <div className="space-y-3" data-testid="reports-feedback-list">
            {(data?.feedbacks || []).length === 0 && (
              <p className="text-sm text-neutral-500">Sin feedback registrado todavía.</p>
            )}
            {(data?.feedbacks || []).map((f) => (
              <div key={f.id} className="glass rounded-2xl p-5 border border-white/10">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < f.rating ? "text-[var(--brand-cyan,#FF85AC)] fill-[var(--brand-cyan,#FF85AC)]" : "text-neutral-700"}`} />
                    ))}
                  </div>
                  <span className="text-[10px] text-neutral-500">{f.agency_name} · {f.created_at}</span>
                </div>
                {f.comment && <p className="text-sm text-neutral-300">{f.comment}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "actividad" && (
          <div className="space-y-3" data-testid="reports-audit-list">
            {auditLog.length === 0 && (
              <p className="text-sm text-neutral-500">Sin actividad registrada todavía.</p>
            )}
            {auditLog.map((a) => (
              <div key={a.id} className="glass rounded-2xl p-4 border border-white/10 flex items-start gap-3">
                <Activity className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-white">
                    {ACTION_LABELS[a.action] || a.action}
                    {a.agency_name && <span className="text-neutral-400"> · {a.agency_name}</span>}
                  </p>
                  {a.detail && <p className="text-xs text-neutral-500">{a.detail}</p>}
                  <p className="text-[10px] text-neutral-600 mt-0.5">{a.created_at}</p>
                </div>
              </div>
            ))}
            {auditLog.length < auditTotal && (
              <button
                onClick={loadMoreAudit}
                disabled={loadingMoreAudit}
                data-testid="load-more-audit-btn"
                className="w-full py-2.5 rounded-full border border-white/15 text-sm text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/40 hover:text-[var(--brand-cyan,#FF85AC)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {loadingMoreAudit && <Loader2 className="w-4 h-4 animate-spin" />}
                Cargar más ({auditTotal - auditLog.length} restantes)
              </button>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
